import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";

/**
 * Thin wrapper over brian's "clinic connector" REST endpoints. The frontend mints
 * and displays a pairing code, lists paired connectors with live status, unpairs
 * one, and fires a test dispatch — it never opens the agent WebSocket nor calls
 * `/connectors/pair` (that's the clinic-side agent's job).
 *
 * Session checks, network failures and non-ok statuses funnel through the shared
 * `request`/`requestVoid` helpers, so every call surfaces the same `ClinicAuthError`
 * vocabulary as the rest of the app. The Bearer token is attached by the auth
 * middleware from the active session.
 */

export type DispatchStatus = components["schemas"]["DispatchResult"]["status"];

/**
 * connected/updating/offline, folding liveness together with the machine's
 * auto-update log server-side. Mirrors brian's `ConnectorStatus`.
 */
export type ConnectorStatus = components["schemas"]["ConnectorStatus"];

/**
 * The statuses this frontend has a word for. Exhaustive by construction: brian
 * adding a fourth `ConnectorStatus` fails the build here (and at the card's
 * `STATUS_WORD`) rather than reaching a lookup with no entry at runtime.
 */
const KNOWN_STATUS: Record<ConnectorStatus, true> = {
  connected: true,
  updating: true,
  offline: true,
};

const isKnownStatus = (value: unknown): value is ConnectorStatus =>
  typeof value === "string" && value in KNOWN_STATUS;

/** What one update cycle did. Mirrors brian's `ConnectorUpdateOutcome`. */
export type ConnectorUpdateOutcome =
  components["schemas"]["ConnectorUpdateOutcome"];

/** One entry of a machine's auto-update history. */
export interface UpdateEvent {
  uuid: string;
  outcome: ConnectorUpdateOutcome;
  fromVersion: string | null;
  toVersion: string | null;
  detail: string;
  occurredAt: string;
}

/** One of a clinic's paired connectors, with live-connection state. */
export interface Connector {
  id: string;
  name: string | null;
  hostname: string;
  agentVersion: string;
  connected: boolean;
  status: ConnectorStatus;
  lastUpdate: UpdateEvent | null;
}

/** A freshly minted pairing code and how long it stays valid. */
export interface MintedCode {
  code: string;
  ttlSeconds: number;
}

/** The outcome of a test dispatch to the clinic's live connector. */
export interface DispatchOutcome {
  status: DispatchStatus;
  output: string;
  error: string | null;
  taskId: string | null;
}

const toUpdateEvent = (
  u: components["schemas"]["UpdateEventSummary"],
): UpdateEvent => ({
  uuid: u.uuid,
  outcome: u.outcome,
  fromVersion: u.from_version ?? null,
  toVersion: u.to_version ?? null,
  detail: u.detail,
  occurredAt: u.occurred_at,
});

const toConnector = (
  c: components["schemas"]["ConnectorSummary"],
): Connector => ({
  id: c.uuid,
  name: c.name ?? null,
  hostname: c.hostname,
  agentVersion: c.agent_version,
  connected: c.connected,
  // Rolling deploys skew both ways, so this tests membership rather than
  // presence. Ahead of the Brian that added `status` the field is simply absent
  // though the generated type calls it required; behind a Brian that adds a
  // fourth one, the value is a word this build has no label for. Both land on
  // the binary `connected` told us before — never on a re-derived `updating`,
  // because the rule that ages an abandoned update out lives server-side and
  // must not be guessed at from here. Resolved at the boundary so
  // `Connector.status` is a guarantee every reader inherits.
  status: isKnownStatus(c.status)
    ? c.status
    : c.connected
      ? "connected"
      : "offline",
  lastUpdate: c.last_update ? toUpdateEvent(c.last_update) : null,
});

const toMintedCode = (
  m: components["schemas"]["MintCodeResponse"],
): MintedCode => ({
  code: m.code,
  ttlSeconds: m.ttl_seconds,
});

const toDispatchOutcome = (
  d: components["schemas"]["DispatchResult"],
): DispatchOutcome => ({
  status: d.status,
  output: d.output,
  error: d.error ?? null,
  taskId: d.task_id ?? null,
});

export interface ConnectorClient {
  /** Mint a single-use pairing code, optionally naming the clinic computer. */
  mintPairingCode(name?: string): Promise<MintedCode>;
  /** List the clinic's paired connectors with live status (clinic-or-user scope). */
  listConnectors(): Promise<Connector[]>;
  /** Unpair a connector by id — closes any live socket, deletes the row. */
  unpair(connectorId: string): Promise<void>;
  /** Send a goal to the clinic's live connector (requires user scope). */
  dispatch(goal: string): Promise<DispatchOutcome>;
}

export const connectorClient: ConnectorClient = {
  mintPairingCode: (name) =>
    request(() =>
      apiClient.POST("/connectors/pair/code", {
        body: { name: name ?? null },
      }),
    ).then(toMintedCode),

  listConnectors: () =>
    request(() => apiClient.GET("/connectors", {})).then((rows) =>
      rows.map(toConnector),
    ),

  unpair: (connectorId) =>
    requestVoid(() =>
      apiClient.DELETE("/connectors/{connector_uuid}", {
        params: { path: { connector_uuid: connectorId } },
      }),
    ),

  dispatch: (goal) =>
    request(() =>
      apiClient.POST("/connectors/dispatch", { body: { goal } }),
    ).then(toDispatchOutcome),
};
